import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';

import { apiClient } from '~api/client';
import type { DisplayHint } from './blast-limits.types';

type EnvironmentQueryParams = {
  /** Name of the blast to query environment sites for */
  blast_name: string;
  /** Buffer distance for the query */
  query_buffer?: number;
};

type EnvironmentBlastDesignConstraints = {
  vibration_limit: string;
  vibration_limit_html: string;
  vibration_limit_units: string;
  minimum_approved_distance: string;
  minimum_approved_distance_html: string;
  minimum_approved_distance_units: string;
  sdob_applicable_limit: string;
  sdob_applicable_limit_html: string;
  sdob_applicable_limit_units: string;
  overpressure_limit: string;
  overpressure_limit_html: string;
  overpressure_limit_units: string;
  dust_control: string;
  dust_control_html: string;
  dust_control_units: string;
  display_hints: DisplayHint;
};

type EnvironmentFeatureProperties = {
  OBJECTID: number;
  SITE_ID: string;
  FEATURE_TYPE: string;
  COMMENTS: string | null;
  distance: number | null;
  blast_design_constraints: EnvironmentBlastDesignConstraints | null;
};

type EnvironmentResponse = FeatureCollection<Polygon | MultiPolygon, EnvironmentFeatureProperties>;

/**
 * Environment service for accessing nearby environment site data based on blast name and optional buffering
 */
const environmentService = {
  /**
   * Get nearby environment sites for a given blast, with optional buffering.
   * @param params - Query parameters for fetching environment data
   * @returns Promise resolving to environment GeoJSON response
   */
  getEnvironment: async (params: EnvironmentQueryParams): Promise<EnvironmentResponse> => {
    const { blast_name, query_buffer = 350 } = params;
    const response = await apiClient.get(`/environment/${encodeURIComponent(blast_name)}`, {
      params: { query_buffer, crs: 'local' },
    });
    return response.data;
  },
};

export type {
  EnvironmentBlastDesignConstraints,
  EnvironmentFeatureProperties,
  EnvironmentQueryParams,
  EnvironmentResponse,
};
export { environmentService };
